import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Container,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    MenuItem as SelectMenuItem,
    Chip,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Fastfood as FastfoodIcon,
    AttachMoney,
    Category as CategoryIcon,
    Receipt as ReceiptIconMui
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { ReportChart } from './ReportChart';
import { formatDateForInput } from '../../utils/dateUtils';

interface FnBItemSalesRow {
    menuItemId: string | null;
    name: string;
    category: string | null;
    isCustom: boolean;
    isArchived: boolean;
    quantitySold: number;
    grossRevenue: number;
    orderCount: number;
}

interface FnBItemSalesReportData {
    startDate: string;
    endDate: string;
    items: FnBItemSalesRow[];
    totals: {
        quantitySold: number;
        grossRevenue: number;
        distinctItems: number;
        orderCount: number;
    };
}

type SortMetric = 'quantitySold' | 'grossRevenue';

const escapeCSVField = (field: any): string => {
    if (field === null || field === undefined) return '""';
    const stringField = String(field);
    return `"${stringField.replace(/"/g, '""')}"`;
};

export const FnBItemSalesReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<FnBItemSalesReportData | null>(null);
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [search, setSearch] = useState('');
    const [sortMetric, setSortMetric] = useState<SortMetric>('quantitySold');

    // Initialize date range to current month
    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    useEffect(() => {
        loadReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);

            const startDateStr = formatDateForInput(startDate);
            const endDateStr = formatDateForInput(endDate);

            const data = await reportAPI.getFnBItemSalesReport({ startDate: startDateStr, endDate: endDateStr });
            setReportData(data);
        } catch (err) {
            setError('Failed to load F&B item sales report data');
            console.error('Error loading F&B item sales report:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const categories = useMemo(() => {
        if (!reportData) return [];
        const set = new Set<string>();
        reportData.items.forEach(i => set.add(i.category || 'Uncategorized'));
        return Array.from(set).sort();
    }, [reportData]);

    const filteredItems = useMemo(() => {
        if (!reportData) return [];
        const searchLower = search.trim().toLowerCase();
        return reportData.items
            .filter(i => categoryFilter === 'all' || (i.category || 'Uncategorized') === categoryFilter)
            .filter(i => !searchLower || i.name.toLowerCase().includes(searchLower))
            .slice()
            .sort((a, b) => b[sortMetric] - a[sortMetric]);
    }, [reportData, categoryFilter, search, sortMetric]);

    const topItems = useMemo(() => filteredItems.slice(0, 10), [filteredItems]);

    const exportToCSV = () => {
        if (!reportData || filteredItems.length === 0) return;

        const headers = ['Item', 'Category', 'Qty Sold', 'Gross Revenue', 'Orders', 'Custom Item', 'Archived'];
        const rows = filteredItems.map(i => [
            escapeCSVField(i.name),
            escapeCSVField(i.category || 'Uncategorized'),
            escapeCSVField(i.quantitySold),
            escapeCSVField(i.grossRevenue.toFixed(2)),
            escapeCSVField(i.orderCount),
            escapeCSVField(i.isCustom ? 'Yes' : 'No'),
            escapeCSVField(i.isArchived ? 'Yes' : 'No')
        ].join(','));

        const csvContent = [headers.map(h => escapeCSVField(h)).join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `fnb-item-sales-report-${reportData.startDate}-to-${reportData.endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading && !reportData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!reportData) {
        return null;
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">F&B Item Sales Report</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                        <Typography variant="h4">F&B Item Sales Report</Typography>
                        <Box display="flex" gap={2} alignItems="center">
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => newValue && setStartDate(newValue)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => newValue && setEndDate(newValue)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                        </Box>
                    </Box>

                    {/* Key Metrics Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <FastfoodIcon sx={{ mr: 1, color: 'success.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Items Sold
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {reportData.totals.quantitySold.toLocaleString()}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <AttachMoney sx={{ mr: 1, color: 'info.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Gross Revenue
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(reportData.totals.grossRevenue)}
                                    </Typography>
                                    <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                        Not netted of refunds
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <CategoryIcon sx={{ mr: 1, color: 'warning.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Distinct Items
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {reportData.totals.distinctItems}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <ReceiptIconMui sx={{ mr: 1, color: 'error.main' }} />
                                        <Typography color="textSecondary" variant="body2">
                                            Orders with Items
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                        {reportData.totals.orderCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Top items chart */}
                    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                            <Typography variant="h6">
                                Top 10 Items by {sortMetric === 'quantitySold' ? 'Quantity Sold' : 'Revenue'}
                            </Typography>
                            <ToggleButtonGroup
                                value={sortMetric}
                                exclusive
                                size="small"
                                onChange={(_, value) => value && setSortMetric(value)}
                            >
                                <ToggleButton value="quantitySold">Quantity</ToggleButton>
                                <ToggleButton value="grossRevenue">Revenue</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {topItems.length > 0 ? (
                            <ReportChart
                                type="bar"
                                data={{
                                    labels: topItems.map(i => i.name),
                                    datasets: [
                                        {
                                            label: sortMetric === 'quantitySold' ? 'Quantity Sold' : 'Gross Revenue',
                                            data: topItems.map(i => i[sortMetric]),
                                            backgroundColor: '#4CAF50'
                                        }
                                    ]
                                }}
                                height={320}
                                options={{
                                    indexAxis: 'y',
                                    scales: {
                                        x: { beginAtZero: true }
                                    },
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            callbacks: {
                                                label: function (context: any) {
                                                    return sortMetric === 'quantitySold'
                                                        ? `Qty Sold: ${context.raw}`
                                                        : `Revenue: ${formatCurrency(context.raw)}`;
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <Typography color="textSecondary" variant="body2">
                                No F&amp;B items sold matching these filters in this date range
                            </Typography>
                        )}
                    </Paper>

                    {/* Item table */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                            <Typography variant="h6">
                                Items ({filteredItems.length})
                            </Typography>
                            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                                <TextField
                                    select
                                    size="small"
                                    label="Category"
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    sx={{ width: 200 }}
                                >
                                    <SelectMenuItem value="all">All Categories</SelectMenuItem>
                                    {categories.map(c => (
                                        <SelectMenuItem key={c} value={c}>{c}</SelectMenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    label="Search item"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    sx={{ width: 200 }}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={exportToCSV}
                                    disabled={filteredItems.length === 0}
                                >
                                    Export CSV
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell align="right">Qty Sold</TableCell>
                                        <TableCell align="right">Gross Revenue</TableCell>
                                        <TableCell align="right">Orders</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredItems.map(i => (
                                        <TableRow key={i.menuItemId ?? `custom:${i.name}`} hover>
                                            <TableCell>
                                                {i.name}
                                                {i.isCustom && (
                                                    <Chip label="Custom" size="small" variant="outlined" sx={{ ml: 1 }} />
                                                )}
                                                {i.isArchived && (
                                                    <Chip label="Archived" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                                                )}
                                            </TableCell>
                                            <TableCell>{i.category || 'Uncategorized'}</TableCell>
                                            <TableCell align="right">{i.quantitySold.toLocaleString()}</TableCell>
                                            <TableCell align="right">{formatCurrency(i.grossRevenue)}</TableCell>
                                            <TableCell align="right">{i.orderCount}</TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredItems.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Typography color="textSecondary" variant="body2" sx={{ py: 2 }}>
                                                    No items match these filters.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </LocalizationProvider>
        </Container>
    );
};
